import type { OptionalKeysOf, RecordKey } from './core.ts';
import { recordEntries } from './stronger_builtins.ts';
import type { TypesEqual } from './type_equality.ts';
import { isAbsent, isPresent } from './type_guards.ts';

// Used to constrain the property value type of static object initializers to `PT`, while preserving
// the union-of-literal type of the keys. N.B. The unusual syntax is a workaround for the current
// lack of partial type inference in TypeScript. Usage:
// ```
//   const config = recordOf<Config>()({
//     a: { ... },
//     b: { ... },
//   });
// ```
// Without any explicit casting, `config` has type `Record<'a' | 'b', Config>`.
//
// Note: After https://github.com/microsoft/TypeScript/pull/26349 lands, replace with:
// ```
//   const config: Record<*, Config> = { ... };
// ```

// TODO: An upcoming version of Typescript is supposed to support inferring later generic params
// while specifying earlier ones. Then this doesn't need to use the double-function-call syntax
export function implementorOf<I>(): <T extends I>(impl: T) => T {
  return (x) => x;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export const recordOf2 = <P>() => implementorOf<Record<RecordKey, P>>();

// TODO: Test?
export function recordOf<P>(): <K extends RecordKey>(
  record: Record<K, P>,
) => Readonly<Record<K, P>> {
  // TODO: An upcoming version of Typescript is supposed to support inferring later generic params
  // while specifying earlier ones. Then this doesn't need to use the double-function-call syntax
  return (record) => Object.freeze(record);
}

// Like `Object.fromEntries`, but with a more accurate tuple type.
// TODO!: Test
export function recordFromEntries<K extends RecordKey, V>(
  entries: Iterable<readonly [K, V]>,
): Partial<Record<K, V>>;
export function recordFromEntries<K extends RecordKey, V>(
  entries: Iterable<readonly [K, V]>,
  qualifier: 'complete',
): Record<K, V>;
export function recordFromEntries<K extends RecordKey, V>(
  entries: Iterable<readonly [K, V]>,
  _qualifier?: 'complete',
): Partial<Record<K, V>> {
  return Object.fromEntries(entries) as Record<K, V>;
}

type Pick<T, K extends keyof T> = {
  [P in K]: T[P];
};

// https://stackoverflow.com/a/56592365/8107589
export function pick<T extends object, K extends keyof T>(obj: T, ...keys: K[]): Pick<T, K>;
export function pick<T extends object, K extends keyof T>(obj: T, ...keys: K[]): Pick<T, K>;
export function pick<T extends object, K extends keyof T>(
  obj: T | undefined,
  ...keys: K[]
): Pick<T, K> | undefined;
export function pick<T extends object, K extends keyof T>(
  obj: T | null,
  ...keys: K[]
): Pick<T, K> | null;
export function pick<T extends object, K extends keyof T>(
  obj: T | undefined | null,
  ...keys: K[]
): Pick<T, K> | undefined | null {
  if (isAbsent(obj)) {
    return obj;
  }
  return Object.fromEntries(keys.filter((key) => key in obj).map((key) => [key, obj[key]])) as Pick<
    T,
    K
  >;
}

export function omit<T extends object, K extends keyof T>(obj: T, ...keys: K[]): Omit<T, K>;
export function omit<T extends object, K extends keyof T>(
  obj: T | undefined,
  ...keys: K[]
): Omit<T, K> | undefined;
export function omit<T extends object, K extends keyof T>(
  obj: T | null,
  ...keys: K[]
): Omit<T, K> | null;
export function omit<T extends object, K extends keyof T>(
  obj: T | undefined | null,
  ...keys: K[]
): Omit<T, K> | undefined | null {
  if (isAbsent(obj)) {
    return obj;
  }
  return Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key as K)),
  ) as Omit<T, K>;
}

export function split<T extends object, K extends keyof T>(
  obj: T,
  ...keys: K[]
): [Pick<T, K>, Omit<T, K>];
export function split<T extends object, K extends keyof T>(
  obj: T | undefined,
  ...keys: K[]
): [Pick<T, K>, Omit<T, K>] | undefined;
export function split<T extends object, K extends keyof T>(
  obj: T | null,
  ...keys: K[]
): [Pick<T, K>, Omit<T, K>] | null;
export function split<T extends object, K extends keyof T>(
  obj: T | undefined | null,
  ...keys: K[]
): [Pick<T, K>, Omit<T, K>] | undefined | null {
  if (isAbsent(obj)) {
    return obj;
  }
  const result = Object.entries(obj).reduce<{
    picked: Array<[K, T[K]]>;
    omitted: Array<[Exclude<keyof T, K>, T[Exclude<keyof T, K>]]>;
  }>(
    (res, prop) => {
      if (keys.includes(prop[0] as K)) {
        res.picked.push(prop as (typeof res.picked)[0]);
      } else {
        res.omitted.push(prop as (typeof res.omitted)[0]);
      }
      return res;
    },
    { picked: [], omitted: [] },
  );
  return [
    Object.fromEntries(result.picked) as Pick<T, K>,
    Object.fromEntries(result.omitted) as Omit<T, K>,
  ];
}

// // TODO!: Test bimap, injection, bijection.
// export function bimap<R>(forward: R) {
//   const entries = recordEntries(forward).map(([k, v]) => [v, k]);
//   const reverse = recordFromEntries(
//     entries as R extends Record<infer K, infer V> ? [K, V][] : never
//   );
//   // TODO: Make this frozen typing simpler with some sort of makeDeepReadonly type transform.
//   return Object.freeze({ forward: Object.freeze(forward), reverse: Object.freeze(reverse) });
// }

// // Like `bimap`, but requires that `Domain` is fully covered by `forward`.
// export function injectiveBimap<Domain extends RecordKey, Range extends RecordKey>(
//   forward: Record<Domain, Range>
// ) {
//   return bimap(forward);
// }

// // Like `bimap`, but requires that both `Domain` and `Range` are fully covered by `forward`.
// export function bijectiveBimap<Domain extends RecordKey, Range extends RecordKey>() {
//   return <R extends Range>(forward: Record<Domain, R>) => bimap(forward);
// }

// TODO!: Test bimap, injection, bijection.
export function bimap<Domain extends RecordKey, Range extends RecordKey>(
  forward: Partial<Record<Domain, Range>> | Record<Domain, Range>,
) {
  const entries = recordEntries(forward, true).map<[Range, Domain]>(([k, v]) => [v, k]);
  const reverse = recordFromEntries(entries);
  // TODO: Make this frozen typing simpler with some sort of makeDeepReadonly type transform.
  return Object.freeze({ forward: Object.freeze(forward), reverse: Object.freeze(reverse) });
}

// Like `bimap`, but requires that `Domain` is fully covered by `forward`.
export function injectiveBimap<Domain extends RecordKey, Range extends RecordKey>(
  forward: Record<Domain, Range>,
) {
  const map = bimap<Domain, Range>(forward);
  type Forward = Readonly<Record<Domain, Range>>;
  type Reverse = typeof map.reverse;
  return map as Readonly<{ forward: Forward; reverse: Reverse }>;
}

// Like `bimap`, but requires that both `Domain` and `Range` are fully covered by `forward`.
export function bijectiveBimap<Domain extends RecordKey, Range extends RecordKey>() {
  return <R extends Range>(forward: Record<Domain, R>) => {
    const map = bimap<Domain, Range>(forward);
    type Forward = Readonly<Record<Domain, Range>>;
    type Reverse = [true] extends [TypesEqual<R, Range>] ? Readonly<Record<Range, Domain>> : never;
    return map as Readonly<{ forward: Forward; reverse: Reverse }>;
  };
}

// // TODO!: Test bimap, injection, bijection.
// export function bimap<Domain extends RecordKey, Range extends RecordKey>(
//   forward: Partial<Record<Domain, Range>>
// ) {
//   const entries = recordEntries(forward).map<[Range, Domain]>(([k, v]) => [v, k]);
//   const reverse = recordFromEntries(entries);
//   // TODO: Make this frozen typing simpler with some sort of makeDeepReadonly type transform.
//   return Object.freeze({ forward: Object.freeze(forward), reverse: Object.freeze(reverse) });
// }

// // Like `bimap`, but requires that `Domain` is fully covered by `forward`.
// export function injectiveBimap<Domain extends RecordKey, Range extends RecordKey>(
//   forward: Record<Domain, Range>
// ) {
//   const map = bimap<Domain, Range>(forward);
//   type Forward = Readonly<Record<Domain, Range>>;
//   type Reverse = typeof map.reverse;
//   return map as Readonly<{ forward: Forward; reverse: Reverse }>;
// }

// // Like `bimap`, but requires that both `Domain` and `Range` are fully covered by `forward`.
// export function bijectiveBimap<Domain extends RecordKey, Range extends RecordKey>() {
//   return <R extends Range>(forward: Record<Domain, R>) => {
//     const map = bimap<Domain, Range>(forward);
//     type Forward = Readonly<Record<Domain, Range>>;
//     type Reverse = [true] extends [TypesEqual<R, Range>] ? Readonly<Record<Range, Domain>> : never;
//     return map as Readonly<{ forward: Forward; reverse: Reverse }>;
//   };
// }

// Map the entries of `record` to an array of transformed values.
export function mapEntries<K extends RecordKey, V, T>(
  record: Partial<Record<K, V>> | Record<K, V>,
  callbackfn: (value: V, key: K) => T,
): T[] {
  return Object.entries(record).map(([key, value]) => callbackfn(value as V, key as K));
}

// Map the property _values_ of a record, while preserving key structure and strong typing. Usage:
// ```
//   const numbers = recordOf<number>({ a: 1, b: 2 });
//   const strings = mapRecord(numbers, (v) => `_${v}_`);
// ```
// `numbers` has type `Record<'a' | 'b', number>`.
// `strings` has type `Record<'a' | 'b', string>` and value `{ a: '_1_', b: '_2_' }`
// TODO: Rewrite the above statements with type, deepEqual assertions in the code above, both for
// clarity and to document how to use those assertions.
export function mapRecord<K extends RecordKey, V1, V2>(
  record: Partial<Record<K, V1>> | Record<K, V1>,
  callbackfn: (value: V1, key: K) => V2,
): Record<K, V2> {
  // https://stackoverflow.com/a/38829074/8107589
  return Object.fromEntries(
    Object.entries(record).map(([key, value]) => [key, callbackfn(value as V1, key as K)]),
  ) as Record<K, V2>;
}

export function mapPartialRecord<K extends RecordKey, V1, V2 extends {}>(
  record: Partial<Record<K, V1>>,
  callbackfn: (value: V1, key: K) => V2 | undefined,
): Partial<Record<K, V2>> {
  // https://stackoverflow.com/a/38829074/8107589
  return recordFromEntries(
    recordEntries(record).flatMap(([key, value]) => {
      const mapped = callbackfn(value, key as K);
      return isPresent(mapped) ? [[key as K, mapped]] : [];
    }),
  );
}

export function mapRecord2<T, V>(
  record: Partial<T>,
  callbackfn: <K extends keyof T>(value: T[K], key: K) => V,
): Partial<Record<keyof T & string, V>> {
  const entries = Object.entries(record) as unknown as ReadonlyArray<
    [keyof T & string, T[keyof T]]
  >;
  return Object.fromEntries(
    entries.map(([key, value]) => [key, callbackfn(value, key)]),
  ) as Partial<Record<keyof T & string, V>>;
}

export function reverseRecord<K extends RecordKey, V extends RecordKey>(
  record: Partial<Record<K, V>> | Record<K, V>,
): Partial<Record<V, K>>;
export function reverseRecord<K extends RecordKey, V extends RecordKey>(
  record: Partial<Record<K, V>> | Record<K, V>,
  qualifier: 'complete',
): Record<V, K>;
export function reverseRecord<K extends RecordKey, V extends RecordKey>(
  record: Partial<Record<K, V>> | Record<K, V>,
  _qualifier?: 'complete',
): Partial<Record<V, K>> | Record<V, K> {
  return Object.fromEntries(Object.entries(record).map(([key, value]) => [value, key])) as Record<
    V,
    K
  >;
}

export function flatmapRecord<K extends RecordKey, V1, V2>(
  record: Partial<Record<K, V1>> | Record<K, V1>,
  callbackfn: (value: V1, key: K) => ReadonlyArray<V2>,
): Partial<Record<K, V2>> {
  const original = recordEntries(record, true);
  const transformed = original.flatMap(([key, value]) =>
    callbackfn(value, key).map<[K, V2]>((v) => [key, v]),
  );
  return recordFromEntries(transformed);
}

export function mapRecordWithProperties<K extends RecordKey, V1, V2>(
  record: Record<K, V1>,
  callbackfn: (value: V1, key: K) => TypedPropertyDescriptor<V2>,
): Record<K, V2> {
  return Object.defineProperties({}, mapRecord(record, callbackfn)) as Record<K, V2>;
}

export function mapRecordWithGetters<K extends RecordKey, V1, V2>(
  record: Record<K, V1>,
  callbackfn: (value: V1, key: K) => () => V2,
): Readonly<Record<K, V2>> {
  return mapRecordWithProperties(record, (value, key) => ({
    get: callbackfn(value, key),
  }));
}

// The same type as T, except optional properties may not have the value `undefined`.
export type ExactOptional<T> = {
  [K in keyof T]: K extends OptionalKeysOf<T> ? Exclude<T[K], undefined> : T[K];
};

// The same type as T, except optional properties may always have the value `undefined`.
export type FullOptional<T> = {
  [K in keyof T]: undefined extends T[K] ? T[K] | undefined : T[K];
};

// https://stackoverflow.com/a/64206553/8107589
type UndefinedKeys<T> = {
  [P in keyof T]: undefined extends T[P] ? P : never;
}[keyof T];

// The same type as T, except properties that can be `undefined` become optional.
export type RelaxedOptional<T> = Partial<Pick<T, UndefinedKeys<T>>> & Omit<T, UndefinedKeys<T>>;

type Pared<T> = {
  [K in keyof T as undefined extends T[K] ? K : never]+?: Exclude<T[K], undefined>;
} & {
  [K in keyof T as undefined extends T[K] ? never : K]: T[K];
};

// Creates a shallow copy of `obj`, omitting any keys whose value is `undefined`.
// This can be useful when exactOptionalPropertyTypes is enabled.
export function pare<T extends object>(obj: T) {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined)) as Pared<T>;
}

// export type NonNullableKeysOf<T> = {
//   [K in keyof T]: NonNullable<T[K]> extends T[K] ? K : never;
// }[keyof T];
export type NonNullableKeysOf<T> = keyof {
  [K in keyof T as undefined extends T[K] ? never : null extends T[K] ? never : K]: true;
};

type ParedPresent<T> = {
  [K in keyof T as K extends NonNullableKeysOf<T> ? never : K]+?: T[K] & {};
} & {
  [K in keyof T as K extends NonNullableKeysOf<T> ? K : never]: T[K];
};

// Like |pare|, except both null and undefined properties are omitted.
export function parePresent<T extends object>(obj: T) {
  return Object.fromEntries(
    Object.entries(obj).filter(([_, v]) => v !== undefined && v !== null),
  ) as ParedPresent<T>;
}

// TODO: Move to Subtle.
export function addSpecialProperties<
  T extends Record<string, unknown>,
  S extends Record<string, unknown>,
>(target: T, source: S): asserts target is T & S {
  Object.defineProperties(
    target,
    // source as Record<keyof K, unknown>
    mapRecord(source, (value) => ({
      value,
      enumerable: false,
      writable: false,
    })),
  );
}
